/**
 * HTTP-клиент Callog API.
 *
 * Токен передаётся заголовком, а не кукой: AllowCredentials на бэкенде
 * выключено, cookie-сессии здесь не будет. Провайдер токена подставляется
 * авторизацией (шаг 2) — клиент про OIDC ничего не знает намеренно.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:8081';

/**
 * Ошибка API. Тело у всех ручек одинаковое: `{ "error": "..." }`.
 *
 * Различать 401 и 403 обязательно: 401 — токена нет или он чужой, надо
 * заново входить; 403 — человек вошёл, но прав нет либо организация не
 * привязана к тенанту, и отправлять его логиниться бессмысленно.
 */
export class ApiError extends Error {
  readonly status: number;

  /**
   * Кто вынес вердикт.
   *
   * `client` — до сервера не дошли (нет токена, сеть). Показывать такой
   * отказ как ответ бэкенда нельзя: подсказки про JWT и audience отправят
   * чинить то, что не сломано.
   */
  readonly origin: 'client' | 'server';

  /** Ответ пришёл в нашем формате `{"error": "..."}`, а не сырым текстом. */
  readonly hasErrorBody: boolean;

  constructor(
    status: number,
    message: string,
    origin: 'client' | 'server' = 'server',
    hasErrorBody = false
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.origin = origin;
    this.hasErrorBody = hasErrorBody;
  }

  /** Сессии нет на нашей стороне — сервер этого запроса не видел. */
  get isMissingSession() {
    return this.origin === 'client' && this.status === 401;
  }

  /** Токена нет, протух или выписан не нам. Отправлять на повторный вход. */
  get isUnauthorized() {
    return this.status === 401;
  }

  /** Нет роли либо организация не подключена. На вход НЕ отправлять. */
  get isForbidden() {
    return this.status === 403;
  }

  /** Объекта нет — либо он в чужом тенанте. Экран одинаковый: так задумано. */
  get isNotFound() {
    return this.status === 404;
  }

  /**
   * Ручки нет в собранном бэкенде.
   *
   * Отличается от обычного 404 телом: наши ручки отвечают `{"error": "..."}`,
   * а промах маршрутизатора — простым текстом «404 page not found». Различие
   * существенное при выкатке, когда фронт и бэк расходятся версиями: «объект
   * не найден» чинится данными, «ручки нет» — пересборкой бэкенда, и путать
   * их значит искать не там.
   */
  get isMissingEndpoint() {
    return this.status === 404 && this.origin === 'server' && !this.hasErrorBody;
  }

  /** Конфликт состояния: занятый добавочный, архивация узла с детьми. */
  get isConflict() {
    return this.status === 409;
  }
}

type TokenProvider = () => string | null | undefined;

let tokenProvider: TokenProvider = () => null;

/** Вызывается один раз из провайдера авторизации. */
export function setTokenProvider(provider: TokenProvider) {
  tokenProvider = provider;
}

/**
 * Собирает query-строку, выбрасывая пустые значения.
 *
 * Пустая строка — это «фильтр не выбран», а не «искать пустоту»: без отсева
 * сброшенный фильтр уехал бы на сервер как `search=` и сузил выдачу.
 */
export function buildQuery(params: Record<string, unknown> | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Тело; сериализуется в JSON. */
  body?: unknown;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
  /** Ручки без токена — только /healthz и /readyz. */
  anonymous?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, anonymous = false } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (!anonymous) {
    const token = tokenProvider();
    // Нет токена — это 401 по сути, и звать API незачем: ответ известен.
    if (!token) throw new ApiError(401, 'Нет действующей сессии', 'client');
    headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}${buildQuery(query)}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    // Сеть или CORS. Отличить их из браузера нельзя, поэтому текст общий.
    if (signal?.aborted) throw cause;
    throw new ApiError(0, 'Не удалось связаться с сервером', 'client');
  }

  if (response.status === 204) return undefined as T;

  const raw = await response.text();
  let parsed: unknown = undefined;
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = undefined;
    }
  }

  if (!response.ok) {
    const hasErrorBody = Boolean(parsed && typeof parsed === 'object' && 'error' in parsed);
    const message =
      (hasErrorBody ? String((parsed as { error: unknown }).error) : '') ||
      defaultMessage(response.status);
    throw new ApiError(response.status, message, 'server', hasErrorBody);
  }

  return parsed as T;
}

/** Запасной текст, когда бэкенд ответил кодом без тела. */
function defaultMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Запрос не разобран';
    case 401:
      return 'Требуется вход';
    case 403:
      return 'Недостаточно прав';
    case 404:
      return 'Не найдено';
    case 409:
      return 'Конфликт состояния';
    default:
      return `Ошибка сервера (${status})`;
  }
}

/**
 * Отказ выгрузки по объёму.
 *
 * Приходит **до** начала файла, поэтому обрабатывается как обычная ошибка, а
 * не как оборванная закачка. Числа в полях нужны в тексте: «слишком много»
 * без «сколько именно и сколько можно» не подсказывает, насколько сужать
 * период.
 */
export class TooManyRowsError extends ApiError {
  readonly rows: number;
  readonly limit: number;

  constructor(message: string, rows: number, limit: number) {
    super(413, message, 'server', true);
    this.name = 'TooManyRowsError';
    this.rows = rows;
    this.limit = limit;
  }
}

/**
 * Скачивание файла.
 *
 * Отдельная функция, а не режим `apiRequest`: тело успешного ответа здесь не
 * JSON, зато тело отказа — JSON, и разбирать надо ровно наоборот.
 *
 * Имя файла берём из `Content-Disposition`: сервер знает и отчёт, и период, а
 * склеенное на клиенте имя разошлось бы с содержимым при первом же
 * несовпадении границ периода.
 */
export async function apiDownload(
  path: string,
  query?: Record<string, unknown>
): Promise<{ blob: Blob; filename: string }> {
  const token = tokenProvider();
  if (!token) throw new ApiError(401, 'Нет действующей сессии', 'client');

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}${buildQuery(query)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new ApiError(0, 'Не удалось связаться с сервером', 'client');
  }

  if (!response.ok) {
    const raw = await response.text();
    let parsed: Record<string, unknown> | undefined;
    try {
      parsed = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      parsed = undefined;
    }
    const hasErrorBody = Boolean(parsed && 'error' in parsed);
    const message = (hasErrorBody ? String(parsed!.error) : '') || defaultMessage(response.status);

    if (response.status === 413) {
      throw new TooManyRowsError(message, Number(parsed?.rows ?? 0), Number(parsed?.limit ?? 0));
    }
    throw new ApiError(response.status, message, 'server', hasErrorBody);
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : 'report.csv';

  return { blob: await response.blob(), filename };
}

/** Пробы живости — без токена, годятся для проверки, что фронт видит бэкенд. */
export const health = {
  live: () => apiRequest<{ status: string }>('/healthz', { anonymous: true }),
  ready: () => apiRequest<{ status: string; database: string }>('/readyz', { anonymous: true }),
};
